import React, { FC, ReactNode, useContext, useMemo, useState, memo, useRef, useCallback, useEffect } from 'react'
import { createContext } from "react";
import { useDispatch, useSelector } from 'react-redux';
import UndoRedoManager from "../../UndoRedoManager/UndoRedoManager";
import { updateChartType } from "../../reducer/chart.reducer";
import { updateChartTimeBucket } from "../../reducer/chart.reducer";
import { updateLayout, updateWidgetType } from "../../reducer/widgetDashboard.reducer";
import { selectActiveLayoutId } from "../../selectors/home";
import { AppContextType } from "../../App";
import Chart from "../chartV2/chart";
import { updatePrimarySecurityAsync } from '../../reducer/home.thunks';
import { setSimulatorOrderDataFunction, setSimulatorTimestamp } from '../../reducer/simulator.reducer';
import { AppDispatch } from '../../store';
export enum ActionType {
    ADD_DRAWING = 'ADD_DRAWING',
    REMOVE_DRAWING = 'REMOVE_DRAWING',
    UPDATE_LAYOUT = 'UPDATE_LAYOUT',
    MULTI_PANE_SELECT = "MULTI_PANE_SELECT",
    ADD_INDICATOR = "ADD_INDICATOR",
    REMOVE_INDICATOR = "REMOVE_INDICATOR",
    TIME_BUCKET = "TIME_BUCKET",
    CHART_TYPE = "CHART_TYPE",
    CANDLE_MOVE_FORWARD_BACKWARD = ' CANDLE_MOVE_FORWARD_BACKWARD',
    ON_TIME_RANGE_CHANGE = 'ON_TIME_RANGE_CHANGE',
    CHANGE_WIDGET_TYPE = ' CHANGE_WIDGET_TYPE',
    ZOOM_IN_OUT = ' ZOOM_IN_OUT',
    SECURITY_CHANGE = 'SECURITY_CHANGE',
    ADD_COMPARE_SECURITY_INDICATOR = 'ADD_COMPARE_SECURITY_INDICATOR',
    REMOVE_COMPARE_SECURITY_INDICATOR = 'REMOVE_COMPARE_SECURITY_INDICATOR',

    SIMULATOR_TIME_CHANGE = 'SIMULATOR_TIME_CHANGE',
    SIMULATOR_ORDERS_CHANGED = 'SIMULATOR_ORDERS_CHANGED',
}
interface UndoRedoContextType<H = any, A = any> {
    manager: UndoRedoManager<H, A>;
    push: (data: H, title?: string, action?: A) => void;
    replace: (data: H, title?: string, action?: A) => void;
    undo: () => H | null;
    redo: () => H | null;
    canUndo: boolean
    canRedo: boolean
}
const UndoRedoContext = createContext<UndoRedoContextType | undefined>(undefined);
export const useUndoRedoContext = () => {
    const context = useContext(UndoRedoContext)
    if (!context) {
        throw new Error("useUndoRedo must be used within an UndoRedoProvider")
    }
    return context
}
const UndoRedoContextProvider: FC<{ children: ReactNode, appContext: AppContextType }> = ({ children, appContext }) => {
    const dispatch = useDispatch<AppDispatch>();
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const [canUndo, setCanUndo] = useState(false)
    const [canRedo, setCanRedo] = useState(false)


    const updateCanUndoRedo = useCallback((canUndo: boolean, canRedo: boolean) => {
        setCanUndo(canUndo)
        setCanRedo(canRedo)
    }, [])

    const manager = useRef(new UndoRedoManager({
        getCanUndoRedoCallback: updateCanUndoRedo
    }));

    const clear = (data: any) => {
        manager.current.clear(data)
    };

    useEffect(() => {
        clear({});
    }, [window.location.pathname]);

    const push = useCallback((data: any, title?: string, action?: ActionType) => {
        manager.current.push(data, title, action);
    }, [])
    const replace = useCallback((data: any) => {
        manager.current.replace(data);
    }, [])


    const undo = useCallback(() => {
        const previousState = manager.current.getCurrentState()
        const state = manager.current.undo();
        if (state) {
            const action = state.action;
            switch (action) {
                case ActionType.UPDATE_LAYOUT:
                    dispatch(updateLayout({ id: activeLayoutId, layout: manager.current.getCurrentState().data }));
                    break;
                case ActionType.ADD_DRAWING:
                    const lastDrawing = previousState.data;
                    if (lastDrawing) {
                        const chart = appContext.getChart(lastDrawing.chartId);
                        if (chart) {
                            chart.drawingManager.removeDrawing(lastDrawing.drawing[0].id, true, false);
                        }
                    }
                    break;
                case ActionType.REMOVE_DRAWING:
                    const drawingToAdd = previousState.data
                    if (drawingToAdd) {
                        const chart = appContext.getChart(drawingToAdd.chartId);
                        if (chart) {
                            drawingToAdd.drawing.forEach((d: any) => {
                                const { id, type, options, settings, state = {}, paneId = "", seriesId = "", } = d
                                chart.drawingManager.addDrawing(
                                    type,
                                    { ...options },
                                    { ...settings },
                                    id,
                                    { ...state },
                                    paneId,
                                    seriesId,
                                );
                            })
                        }
                    }
                    break;
                case ActionType.ADD_INDICATOR:
                    const AddIndicatorData = previousState.data[previousState.data.length - 1]
                    if (AddIndicatorData) {
                        const chart = appContext.getChart(AddIndicatorData.chartId)
                        if (chart) {
                            const addPane = AddIndicatorData.indicator.type !== 'OVERLAY';
                            if (addPane) {
                                const paneIndex = chart.chartConfig.panes.indexOf(AddIndicatorData?.paneId ?? '')
                                if (paneIndex !== -1) {
                                    chart.removePane(paneIndex, true, true)
                                } else {
                                    console.error('Pane for the given pane/seriesGroupId not found');
                                }
                            } else {
                                chart.removeSeries(AddIndicatorData.seriesGroupId, true, true);
                            }
                        }
                    }
                    break;
                case ActionType.REMOVE_INDICATOR:
                    const AddIndicators = previousState.data
                    const chart = appContext.getChart(AddIndicators.chartId);
                    if (chart) {
                        const initialPanes = [...chart.chartConfig.panes];

                        chart.addIndicator(AddIndicators.indicator, true);

                        const newSeriesGroupId = Object.keys(chart.chartConfig.seriesGroups).find(sgId => {
                            const seriesGroup = chart.chartConfig.seriesGroups[sgId];
                            return seriesGroup.scriptCode === AddIndicators.indicator.code;
                        });

                        if (newSeriesGroupId) {
                            AddIndicators.seriesGroupId = newSeriesGroupId;
                        }
                        const updatedPanes = chart.chartConfig.panes;

                        const newPaneId = updatedPanes.find(paneId => !initialPanes.includes(paneId) && paneId !== "");

                        if (newPaneId && AddIndicators.indicator.type !== 'OVERLAY') {
                            AddIndicators.paneId = newPaneId;
                        }
                    }
                    break;
                case ActionType.ADD_COMPARE_SECURITY_INDICATOR: {
                    const addComparableSecurity = previousState.data[previousState.data.length - 1];
                    if (addComparableSecurity) {
                        const chart = appContext.getChart(addComparableSecurity.chartId);
                        if (chart) {
                            const paneIndex = chart.chartConfig.panes.indexOf(addComparableSecurity?.paneId ?? '');
                            if (paneIndex !== -1) {

                                chart.removeComparableSecurity(addComparableSecurity.seriesGroupId, true, true);
                            } else {
                                console.error(`Pane for paneId: ${addComparableSecurity.paneId} not found`);
                            }
                        } else {
                            console.error(`Chart not found for chartId: ${addComparableSecurity.chartId}`);
                        }
                    } else {
                        console.error('addComparableSecurity data is missing');
                    }
                    break;
                }
                case ActionType.REMOVE_COMPARE_SECURITY_INDICATOR:
                    const removeComparable = previousState.data
                    if (removeComparable) {
                        const chart = appContext.getChart(removeComparable[0].chartId);
                      if (chart) {
                           const securities = Array.isArray(removeComparable.security)
                                ? removeComparable[0].security
                                : [removeComparable[0].security];
                            chart.updateComparableSecurities(securities, true);
                            const newSeriesGroupId = Object.keys(chart.chartConfig.seriesGroups).find(sgId => {
                                const seriesGroup = chart.chartConfig.seriesGroups[sgId];
                                return seriesGroup.scriptCode === removeComparable[0].security.code;
                            });
                            if (newSeriesGroupId) {
                                removeComparable[0].seriesGroupId = newSeriesGroupId;
                            }
                        }
                    }
                    break;

                case ActionType.TIME_BUCKET:
                    dispatch(updateChartTimeBucket({ viewKey: previousState.data[0].chartId, timeBucket: previousState.data[0].previousData }));
                    break;
                case ActionType.MULTI_PANE_SELECT:
                    dispatch(updateLayout({ id: state.data[0].activeLayoutId, layout: state.data[0].previousData }))
                    break
                case ActionType.CHART_TYPE:
                    dispatch(updateChartType({ viewKey: previousState.data[0].chartID, chartType: previousState.data[0].previousData }))
                    break;
                case ActionType.CANDLE_MOVE_FORWARD_BACKWARD:
                    const chartData = previousState.data
                    if (chartData) {
                        const chart = appContext.getChart(chartData.chartId)
                        if (chart) {
                            const timeScale = chart.chartApi.timeScale();
                            const visibleRange = timeScale.getVisibleLogicalRange();
                            if (visibleRange) {
                                setVisibleLogicalRange(chart, previousState.data.previousData)
                            }
                        }
                    }
                    break;
                case ActionType.ON_TIME_RANGE_CHANGE:
                    const timeData = previousState.data
                    if (timeData) {
                        const chart = appContext.getChart(timeData.chartId)
                        if (chart) {
                            const timeScale = chart.chartApi.timeScale();
                            const visibleRange = timeScale.getVisibleLogicalRange();
                            if (visibleRange) {
                                setVisibleLogicalRange(chart, timeData.previousData)
                            }
                        }
                    }
                    break;
                case ActionType.CHANGE_WIDGET_TYPE:
                    const WidgetTypeData = previousState.data
                    dispatch(updateWidgetType({ id: WidgetTypeData.chartId, slotId: WidgetTypeData.slotId, type: WidgetTypeData.previousType }))
                    break;
                case ActionType.ZOOM_IN_OUT:
                    const zoomInOutData = previousState.data
                    if (zoomInOutData) {
                        const chart = appContext.getChart(zoomInOutData.chartId)
                        if (chart) {
                            const timeScale = chart.chartApi.timeScale();
                            const visibleRange = timeScale.getVisibleLogicalRange();
                            if (visibleRange) {
                                setVisibleLogicalRange(chart, zoomInOutData.previousData)
                            }
                        }
                    }
                    break;
                case ActionType.SECURITY_CHANGE:
                    const historyData = previousState.data;
                    if (historyData) {
                        dispatch(updatePrimarySecurityAsync({
                            key: historyData[0].previousData.key,
                            expressionMode: historyData[0].previousData.expressionMode,
                            securities: historyData[0].previousData.securities,
                            underlyingSecurityCode: historyData[0].previousData.underlyingSecurityCode
                        }));
                    }
                    break;
                case ActionType.SIMULATOR_TIME_CHANGE:
                    dispatch(setSimulatorTimestamp(previousState.data.previousTimeStamp))
                    break;
                case ActionType.SIMULATOR_ORDERS_CHANGED:
                    setSimulatorOrderDataFunction({
                        orders: previousState.data.prev.orders,
                        orderPrices: previousState.data.prev.ordersPrices
                    })
                    break;
                default:
                    console.warn('Unknown action type during undo:', action);
            }
        }
        return state
    }, [activeLayoutId, appContext])


    const redo = useCallback(() => {
        const state = manager.current.redo();
        if (state) {
            const action = state.action;
            switch (action) {
                case ActionType.UPDATE_LAYOUT:
                    dispatch(updateLayout({ id: activeLayoutId, layout: manager.current.getCurrentState().data }));
                    break;
                case ActionType.ADD_DRAWING:
                    const drawingToAdd = state.data
                    if (drawingToAdd) {
                        const chart = appContext.getChart(drawingToAdd.chartId);
                        if (chart) {
                            const { id, type, options, settings, state = {}, paneId = "", seriesId = "", } = drawingToAdd.drawing[0];
                            chart.drawingManager.addDrawing(
                                type,
                                { ...options },
                                { ...settings },
                                id,
                                { ...state },
                                paneId,
                                seriesId,
                            );
                        }
                    }
                    break;
                case ActionType.REMOVE_DRAWING:
                    const lastRemovedDrawing = state.data
                    if (lastRemovedDrawing) {
                        const chart = appContext.getChart(lastRemovedDrawing.chartId)
                        if (chart) {
                            lastRemovedDrawing.drawing.forEach((d: any) => {
                                const { id } = d
                                chart.drawingManager.removeDrawing(id, true, false);
                            })
                        }
                    }
                    break;
                case ActionType.ADD_INDICATOR: {
                    const AddIndicators = state.data[state.data.length - 1];
                    const chart = appContext.getChart(AddIndicators.chartId);
                    if (chart) {
                        const initialPanes = [...chart.chartConfig.panes];
                        chart.addIndicator(AddIndicators.indicator, true);

                        const newSeriesGroupId = Object.keys(chart.chartConfig.seriesGroups).find(sgId => {
                            const seriesGroup = chart.chartConfig.seriesGroups[sgId];
                            return seriesGroup.scriptCode === AddIndicators.indicator.code;
                        });
                        if (newSeriesGroupId) {
                            AddIndicators.seriesGroupId = newSeriesGroupId;
                        }
                        const updatedPanes = chart.chartConfig.panes;

                        const newPaneId = updatedPanes.find(paneId => !initialPanes.includes(paneId) && paneId !== "");

                        if (newPaneId && AddIndicators.indicator.type !== 'OVERLAY') {
                            AddIndicators.paneId = newPaneId;
                        }
                    }

                    break;
                }
                case ActionType.REMOVE_INDICATOR:
                    const removeIndicatorData = state.data
                    if (removeIndicatorData) {
                        const chart = appContext.getChart(removeIndicatorData.chartId)
                        if (chart) {
                            const addPane = removeIndicatorData.indicator.type !== 'OVERLAY';
                            if (addPane) {
                                const paneIndex = chart.chartConfig.panes.indexOf(removeIndicatorData?.paneId ?? '')
                                if (paneIndex !== -1) {
                                    chart.removePane(paneIndex, true, true);
                                } else {
                                    console.error('Pane for the given seriesGroupId not found');
                                }
                            } else {
                                chart.removeSeries(removeIndicatorData.seriesGroupId, true, true);
                            }
                        }
                    }
                    break;
                case ActionType.ADD_COMPARE_SECURITY_INDICATOR:
                    const addComparableSecurity = state.data[state.data.length - 1];
                    if (addComparableSecurity) {
                        const chart = appContext.getChart(addComparableSecurity.chartId)
                        const securities = Array.isArray(addComparableSecurity.security)
                            ? addComparableSecurity.security
                            : [addComparableSecurity.security];
                           chart.updateComparableSecurities(securities, true);
                        const newSeriesGroupId = Object.keys(chart.chartConfig.seriesGroups).find(sgId => {
                            const seriesGroup = chart.chartConfig.seriesGroups[sgId];
                            return seriesGroup.scriptCode === addComparableSecurity.security.code;
                        });
                        if (newSeriesGroupId) {
                            addComparableSecurity.seriesGroupId = newSeriesGroupId;
                        }
                    }
                    break;

                case ActionType.REMOVE_COMPARE_SECURITY_INDICATOR: {
                    const removeComparableSecurity = state.data;
                    if (removeComparableSecurity) {
                        const chart = appContext.getChart(removeComparableSecurity[0].chartId);
                        if (chart) {
                            const paneIndex = chart.chartConfig.panes.indexOf(removeComparableSecurity[0]?.paneId ?? '');
                            if (paneIndex !== -1) {
                               chart.removeComparableSecurity(removeComparableSecurity[0].seriesGroupId, true, true);
                            } else {
                                console.error(`Pane for paneId: ${removeComparableSecurity[0].paneId} not found`);
                            }
                        } else {
                            console.error(`Chart not found for chartId: ${removeComparableSecurity[0].chartId}`);
                        }
                    } else {
                        console.error('removeComparableSecurity data is missing');
                    }
                    break;
                }

                case ActionType.TIME_BUCKET:
                    dispatch(updateChartTimeBucket({ viewKey: state.data[0].chartId, timeBucket: state.data[0].currentData }))
                    break;
                case ActionType.MULTI_PANE_SELECT:
                    dispatch(updateLayout({ id: state.data[0].activeLayoutId, layout: state.data[0].current }))
                    break
                case ActionType.CHART_TYPE:
                    dispatch(updateChartType({ viewKey: state.data[0].chartID, chartType: state.data[0].currentData }))
                    break;
                case ActionType.CANDLE_MOVE_FORWARD_BACKWARD:
                    const chartData = state.data
                    if (chartData) {
                        const chart = appContext.getChart(chartData.chartId)
                        if (chart) {
                            const timeScale = chart.chartApi.timeScale();
                            const visibleRange = timeScale.getVisibleLogicalRange();
                            if (visibleRange) {
                                setVisibleLogicalRange(chart, state.data.currentData)
                            }
                        }
                    }
                    break;
                case ActionType.ON_TIME_RANGE_CHANGE:
                    const timeData = state.data
                    if (timeData) {
                        const chart = appContext.getChart(timeData.chartId)
                        if (chart) {
                            const timeScale = chart.chartApi.timeScale();
                            const visibleRange = timeScale.getVisibleLogicalRange();
                            if (visibleRange) {
                                setVisibleLogicalRange(chart, timeData.currentData)
                            }
                        }
                    }
                    break;
                case ActionType.CHANGE_WIDGET_TYPE:
                    const WidgetTypeData = state.data
                    dispatch(updateWidgetType({ id: WidgetTypeData.chartId, slotId: WidgetTypeData.slotId, type: WidgetTypeData.currentType }))
                    break
                case ActionType.ZOOM_IN_OUT:
                    const zoomInOutData = state.data
                    if (zoomInOutData) {
                        const chart = appContext.getChart(zoomInOutData.chartId)
                        if (chart) {
                            const timeScale = chart.chartApi.timeScale();
                            const visibleRange = timeScale.getVisibleLogicalRange();
                            if (visibleRange) {
                                setVisibleLogicalRange(chart, zoomInOutData.currentData)
                            }
                        }
                    }
                    break;
                case ActionType.SECURITY_CHANGE:
                    const redoHistoryData = state.data;
                    if (redoHistoryData) {
                        dispatch(updatePrimarySecurityAsync({
                            key: redoHistoryData[0].currentData.key,
                            expressionMode: redoHistoryData[0].currentData.expressionMode,
                            securities: redoHistoryData[0].currentData.securities,
                            underlyingSecurityCode: redoHistoryData[0].currentData.underlyingSecurityCode
                        }));
                    }
                    break;
                case ActionType.SIMULATOR_TIME_CHANGE:
                    dispatch(setSimulatorTimestamp(state.data.timeStamp))
                    break;
                case ActionType.SIMULATOR_ORDERS_CHANGED:
                    setSimulatorOrderDataFunction({
                        orders: state.data.new.orders,
                        orderPrices: state.data.new.ordersPrices
                    })
                    break;
                default:
                    console.warn('Unknown action type during undo:', action);
            }
        }
        return state;
    }, [activeLayoutId, appContext])

    const canUndoRef = useRef(canUndo)
    const canRedoRef = useRef(canRedo)

    useEffect(() => {
        canUndoRef.current = canUndo
    }, [canUndo])

    useEffect(() => {
        canRedoRef.current = canRedo
    }, [canRedo])

    useEffect(() => {
        const cm = window.chartManager
        if (!cm || typeof cm.setUndoRedoBridge !== 'function') {
            return
        }
        cm.setUndoRedoBridge({
            undo: () => {
                undo()
            },
            redo: () => {
                redo()
            },
            getCanUndo: () => canUndoRef.current,
            getCanRedo: () => canRedoRef.current,
            // Expose `push` so `ChartManager` (native-invoked commands) can record history that
            // would otherwise bypass `useUndoRedoContext().push` from web components.
            push: (data: any, title?: string, action?: ActionType) => {
                manager.current.push(data, title, action)
            }
        })
        return () => {
            cm.setUndoRedoBridge(null)
        }
    }, [undo, redo])

    const contextValue: UndoRedoContextType<any, ActionType> = useMemo(() => ({
        manager: manager.current,
        push,
        undo,
        redo,
        replace,
        canUndo: canUndo,
        canRedo: canRedo
    }), [canUndo, canRedo]);
    return (
        <UndoRedoContext.Provider value={contextValue}>
            {children}
        </UndoRedoContext.Provider>
    );
};

const setVisibleLogicalRange = (chart: Chart, data: any) => {
    chart.silentUpdateFromHistory = true
    if (data) {
        chart.focusTimeRange(data, chart.timeBucket, undefined)
    } else {
        chart.resetTimeScale()
    }
}
export default memo(UndoRedoContextProvider)
